/**
 * 支付失败页 agent-tip：failed_type → 文案 key / 按钮 / 埋点
 * failed_type 与接口 payment_assistant/get_orders 字段一致
 * 文案本体在 payAssistant.js DEFAULT_ENTRY_TEXTS（按语言配置，无则不展示）
 */

/** @typedef {'switch_paytype' | 'contact_service'} PayFailTipAction */

/**
 * @typedef {Object} PayFailTipButtonSchema
 * @property {string} labelKey entryTexts 字段名
 * @property {PayFailTipAction} action
 */

/**
 * @typedef {Object} PayFailTipTypeSchema
 * @property {string} messageKey entryTexts 字段名
 * @property {PayFailTipButtonSchema} primary
 * @property {PayFailTipButtonSchema | null} secondary
 * @property {string} trackEvent
 */

/**
 * @typedef {Object} PayFailTipButton
 * @property {string} label
 * @property {PayFailTipAction} action
 */

/**
 * @typedef {Object} PayFailTipUiConfig
 * @property {string} message
 * @property {PayFailTipButton} primary
 * @property {PayFailTipButton | null} secondary
 * @property {string} trackEvent
 */

/** @type {Record<string, PayFailTipTypeSchema>} */
export const PAY_FAIL_TIP_BY_TYPE = {
  BALANCE_ERROR: {
    messageKey: 'payFailBalanceErrorMessage',
    primary: { labelKey: 'payFailSwitchPaytypeBtn', action: 'switch_paytype' },
    secondary: null,
    trackEvent: 'payment_fail_balance',
  },
  CARD_EXPIRED: {
    messageKey: 'payFailCardExpiredMessage',
    primary: { labelKey: 'payFailSwitchPaytypeBtn', action: 'switch_paytype' },
    secondary: null,
    trackEvent: 'payment_fail_card_expired',
  },
  CARD_LOST_STOLEN: {
    messageKey: 'payFailCardLostStolenMessage',
    primary: { labelKey: 'payFailSwitchPaytypeBtn', action: 'switch_paytype' },
    secondary: {
      labelKey: 'payFailContactServiceBtn',
      action: 'contact_service',
    },
    trackEvent: 'payment_fail_card_lost',
  },
  CARD_INVALID: {
    messageKey: 'payFailCardInvalidMessage',
    primary: { labelKey: 'payFailSwitchPaytypeBtn', action: 'switch_paytype' },
    secondary: null,
    trackEvent: 'payment_fail_card_invalid',
  },
  RISK_BLOCK: {
    messageKey: 'payFailRiskBlockMessage',
    primary: { labelKey: 'payFailSwitchPaytypeBtn', action: 'switch_paytype' },
    secondary: {
      labelKey: 'payFailContactServiceBtn',
      action: 'contact_service',
    },
    trackEvent: 'payment_fail_risk',
  },
  ISSUER_TIMEOUT: {
    messageKey: 'payFailIssuerTimeoutMessage',
    primary: { labelKey: 'payFailSwitchPaytypeBtn', action: 'switch_paytype' },
    secondary: null,
    trackEvent: 'payment_fail_timeout',
  },
  GENERAL_DECLINE: {
    messageKey: 'payFailGeneralDeclineMessage',
    primary: { labelKey: 'payFailSwitchPaytypeBtn', action: 'switch_paytype' },
    secondary: {
      labelKey: 'payFailContactServiceBtn',
      action: 'contact_service',
    },
    trackEvent: 'payment_fail_general',
  },
};

const DEFAULT_FAIL_TYPE = 'GENERAL_DECLINE';

/**
 * @param {PayFailTipButtonSchema | null} btn
 * @param {Object} texts
 * @returns {PayFailTipButton | null}
 */
function resolveButton(btn, texts) {
  if (!btn) return null;
  return {
    label: texts[btn.labelKey] || '',
    action: btn.action,
  };
}

/**
 * @param {string} [failedType]
 * @param {Object} [texts] 当前语言 entryTexts（来自 getEntryTexts）
 * @returns {PayFailTipUiConfig & { failType: string }}
 */
export function resolvePayFailTipConfig(failedType, texts = {}) {
  const key =
    failedType && PAY_FAIL_TIP_BY_TYPE[failedType]
      ? failedType
      : DEFAULT_FAIL_TYPE;
  const schema = PAY_FAIL_TIP_BY_TYPE[key];
  return {
    failType: key,
    message: texts[schema.messageKey] || '',
    primary: resolveButton(schema.primary, texts),
    secondary: resolveButton(schema.secondary, texts),
    trackEvent: schema.trackEvent,
  };
}
